import { useState } from 'react';
import { Send, Calendar, CreditCard, Check } from 'lucide-react';
import { siteConfig } from '@/config/site';
import { useLanguage } from '@/context/LanguageContext';
import { api } from '@/services/api';
import ConsentCheckboxes from './ConsentCheckboxes';
import AvailabilitySlotPicker from './AvailabilitySlotPicker';

const PAYMENT_METHODS = [
  { id: 'cashapp', label: 'Cash App' },
  { id: 'chime', label: 'Chime' },
  { id: 'applepay', label: 'Apple Pay' },
  { id: 'tap2pay', label: 'Tap2Pay' },
  { id: 'creditcard', label: 'Credit Card' },
  { id: 'cash', label: 'Cash' },
];

export function BookingSection() {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    datetime: '',
    datetime_formatted: '',
    service: 'HH',
    payment_method: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [conflictMessage, setConflictMessage] = useState(null);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const content = {
    en: {
      subtitle: 'Ready to Experience',
      title: 'Request Your Session',
      description: 'Fill out the form to request your experience. We will contact you within 2 hours to confirm availability.',
      fields: {
        name: 'Name / Alias',
        phone: 'Phone Number',
        phoneHint: 'Required for confirmation',
        email: 'Email',
        emailHint: 'Optional',
        datetime: 'Preferred Date & Time',
        payment: 'Payment Method',
        paymentRequired: 'Required to submit',
        notes: 'Special Requests / Questions',
        notesHint: 'Optional - Photo/video uploads welcome',
        submit: 'Submit Request',
      },
      selectDateTime: 'Select Date & Time',
      paymentInfo: {
        title: 'Payment Information',
        note1: 'Payment is not due until time of arrival.',
        note2: 'If a deposit is required for your booking, details will be provided through customer service communication.',
      },
      success: {
        title: 'Request Received!',
        message: 'Thank you for your interest. We will contact you within 2 hours to confirm your appointment.',
      },
    },
    es: {
      subtitle: 'Listo para Experimentar',
      title: 'Solicita Tu Sesión',
      description: 'Completa el formulario para solicitar tu experiencia. Te contactaremos dentro de 2 horas para confirmar disponibilidad.',
      fields: {
        name: 'Nombre / Alias',
        phone: 'Número de Teléfono',
        phoneHint: 'Requerido para confirmación',
        email: 'Email',
        emailHint: 'Opcional',
        datetime: 'Fecha y Hora Preferida',
        payment: 'Método de Pago',
        paymentRequired: 'Requerido para enviar',
        notes: 'Solicitudes Especiales / Preguntas',
        notesHint: 'Opcional - Fotos/videos bienvenidos',
        submit: 'Enviar Solicitud',
      },
      selectDateTime: 'Seleccionar Fecha y Hora',
      paymentInfo: {
        title: 'Información de Pago',
        note1: 'El pago no se debe hasta el momento de la llegada.',
        note2: 'Si se requiere un depósito para tu reserva, los detalles se proporcionarán a través de comunicación con servicio al cliente.',
      },
      success: {
        title: '¡Solicitud Recibida!',
        message: 'Gracias por tu interés. Te contactaremos dentro de 2 horas para confirmar tu cita.',
      },
    },
  };

  const t = content[language];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePaymentSelect = (methodId) => {
    setFormData(prev => ({ ...prev, payment_method: methodId }));
  };

  const isFormValid = formData.name && formData.phone && formData.datetime && formData.payment_method && ageConfirmed && termsAccepted;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid) {
      return;
    }
    
    setIsSubmitting(true);

    try {
      await api.createBooking({
        name: formData.name,
        phone: formData.phone,
        contact_method: formData.email ? 'email' : 'phone',
        preferred_datetime: formData.datetime,
        start_at: formData.datetime,
        service_code: formData.service,
        session_type: 'in-call',
        notes: formData.notes || '',
        age_confirmed: ageConfirmed,
        terms_accepted: termsAccepted,
        consent_version: 'v1.2026.02',
      });
      
      api.trackEvent('form_submit', 'booking_submit', '/client');
      setIsSuccess(true);
    } catch (error) {
      // Surface 409 conflicts in-line so the customer can pick another slot
      const status = error?.response?.status || error?.status;
      const detail = error?.response?.data?.detail || error?.data?.detail;
      if (status === 409 || (detail && /no longer available/i.test(detail))) {
        setConflictMessage(
          detail || (language === 'en'
            ? 'That time is no longer available. Please choose another available option.'
            : 'Ese horario ya no está disponible. Por favor elige otra opción disponible.')
        );
        setFormData((f) => ({ ...f, datetime: '', datetime_formatted: '' }));
      } else {
        console.error('Booking error:', error);
        alert(language === 'en' ? 'Error submitting request. Please try again.' : 'Error al enviar solicitud. Por favor intenta de nuevo.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section id="book" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="booking-success">
        <div className="max-w-xl mx-auto">
          <div className="neon-card-glow text-center py-10">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#27D7FF] to-[#8B5CFF] flex items-center justify-center mx-auto mb-4">
              <Send className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-heading text-2xl font-bold text-[#F4F2FF] mb-2">
              {t.success.title}
            </h3>
            <p className="text-[#A7A3BA]">
              {t.success.message}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="book" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="booking-section">
      <div className="max-w-xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10">
          <p className="text-[#27D7FF] text-sm font-semibold uppercase tracking-widest mb-2">
            {t.subtitle}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[#F4F2FF] mb-4">
            {t.title}
          </h2>
          <p className="text-[#A7A3BA] max-w-lg mx-auto">
            {t.description}
          </p>
        </div>

        {/* Booking Form */}
        <form onSubmit={handleSubmit} className="neon-card" data-testid="booking-form">
          <div className="space-y-5">
            {/* Name - Required */}
            <div>
              <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                {t.fields.name} <span className="text-[#FF4DA6]">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="input-neon"
                placeholder="John"
                data-testid="booking-name-input"
              />
            </div>

            {/* Phone - Required */}
            <div>
              <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                {t.fields.phone} <span className="text-[#FF4DA6]">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="input-neon"
                placeholder="(555) 555-5555"
                data-testid="booking-phone-input"
              />
              <p className="text-xs text-[#A7A3BA]/60 mt-1">{t.fields.phoneHint}</p>
            </div>

            {/* Email - Optional */}
            <div>
              <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                {t.fields.email} <span className="text-[#A7A3BA]/50 text-xs">({t.fields.emailHint})</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input-neon"
                placeholder="email@example.com"
                data-testid="booking-email-input"
              />
            </div>

            {/* Real availability picker — only shows times the backend confirms are free */}
            <div>
              <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                {t.fields.datetime} <span className="text-[#FF4DA6]">*</span>
              </label>
              <AvailabilitySlotPicker
                serviceCode={formData.service}
                value={formData.datetime}
                onChange={(iso) => {
                  setConflictMessage(null);
                  setFormData((f) => ({
                    ...f,
                    datetime: iso || '',
                    datetime_formatted: iso ? new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '',
                  }));
                }}
                conflictMessage={conflictMessage}
              />
            </div>

            {/* Payment Method - Required */}
            <div>
              <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                {t.fields.payment} <span className="text-[#FF4DA6]">*</span>
              </label>
              <p className="text-xs text-[#A7A3BA]/60 mb-3">{t.fields.paymentRequired}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {PAYMENT_METHODS.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => handlePaymentSelect(method.id)}
                    className={`py-2.5 px-3 rounded-xl border text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                      formData.payment_method === method.id
                        ? 'bg-gradient-to-r from-[#27D7FF]/20 to-[#8B5CFF]/20 border-[#27D7FF] text-[#27D7FF]'
                        : 'bg-[#0B0A12] border-white/10 text-[#A7A3BA] hover:border-[#27D7FF]/50'
                    }`}
                    data-testid={`payment-${method.id}`}
                  >
                    {formData.payment_method === method.id && <Check className="w-3 h-3" />}
                    {method.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Info Box */}
            <div className="p-4 rounded-xl bg-[#0B0A12] border border-[#27D7FF]/20">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-4 h-4 text-[#27D7FF]" />
                <span className="text-sm font-medium text-[#27D7FF]">{t.paymentInfo.title}</span>
              </div>
              <p className="text-[#A7A3BA] text-xs mb-1">• {t.paymentInfo.note1}</p>
              <p className="text-[#A7A3BA] text-xs">• {t.paymentInfo.note2}</p>
            </div>

            {/* Notes - Optional */}
            <div>
              <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                {t.fields.notes} <span className="text-[#A7A3BA]/50 text-xs">({t.fields.notesHint})</span>
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="input-neon resize-none"
                placeholder={language === 'en' ? 'Any special requests or questions?' : '¿Alguna solicitud especial o pregunta?'}
                data-testid="booking-notes-input"
              />
            </div>

            {/* Required consent — 18+ and Terms/Privacy/NDA */}
            <ConsentCheckboxes
              ageConfirmed={ageConfirmed}
              setAgeConfirmed={setAgeConfirmed}
              termsAccepted={termsAccepted}
              setTermsAccepted={setTermsAccepted}
              testIdPrefix="booking-consent"
              className="pt-2"
            />

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting || !isFormValid}
              className="btn-neon btn-neon-pink w-full mt-4"
              data-testid="booking-submit-btn"
            >
              {isSubmitting ? (
                <span>{language === 'en' ? 'Sending...' : 'Enviando...'}</span>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>{t.fields.submit}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

export default BookingSection;
